import React from 'react';
import { Box, HStack, Text } from '@chakra-ui/react';
import { IconButton } from '@/components/ui/icon-button';
import { LuX } from 'react-icons/lu';
import { TaskComposerController } from './useTaskComposer.ts';
import ComposerTaskEditor from './ComposerTaskEditor.tsx';
import ComposerChildTasks from './ComposerChildTasks.tsx';

interface ComposerTaskFormProps {
	task: TaskComposerController;
	shouldFocus: boolean;
	showChildTasks?: boolean;
	onClearParentTask?: () => void;
}

/**
 * Task tab body — just title/details. The due date and repeat toggle live in the composer action
 * bar, and the recurrence interval expands as a row beneath that bar (ComposerFrequencyReveal).
 */
const ComposerTaskForm: React.FC<ComposerTaskFormProps> = (props) => {
	const { task, shouldFocus, showChildTasks = true, onClearParentTask } = props;

	return (
		<Box px={4} pb={4}>
			{task.parentTask ? (
				<HStack
					mb={2}
					w="fit-content"
					maxW="full"
					h={7}
					gap={1}
					pl={2.5}
					pr={1}
					border="1px solid"
					borderColor="border.default"
					borderRadius="full"
					bg="surface.subtle"
					color="brand.fg"
				>
					<Text minW={0} textStyle="label.sm" truncate>
						Sub-task of {task.parentTask.title}
					</Text>
					{onClearParentTask ? (
						<IconButton
							size="xs"
							h={5}
							minW={5}
							w={5}
							variant="ghost"
							color="fg.muted"
							aria-label={`Stop adding a sub-task to ${task.parentTask.title}`}
							onClick={onClearParentTask}
							_hover={{ bg: 'surface.default', color: 'fg' }}
						>
							<LuX />
						</IconButton>
					) : null}
				</HStack>
			) : null}
			<ComposerTaskEditor
				title={task.title}
				description={task.description}
				onTitleChange={task.setTitle}
				onDescriptionChange={task.setDescription}
				disabled={task.isSubmitting}
				shouldFocus={shouldFocus}
			/>
			{task.childCreationEnabled && showChildTasks && <ComposerChildTasks task={task} />}
		</Box>
	);
};

export default ComposerTaskForm;
